import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Link, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { integrationApiService } from '../services/integrationApi';
import { DateField, FormDialog } from './ui';
import {
  chipTone,
  ellipsis,
  ingest,
  primaryButton,
  rowButton,
  secondaryButton,
  tabular,
} from './dataIngestion/ingestionTokens';
import { tokens } from '../theme/tokens';

type SignalFilter = 'open' | 'queued' | 'all';

type SignalRow = {
  id: string;
  brandName?: string;
  brandId?: string;
  title?: string;
  articleUrl?: string;
  productUrl?: string;
  status?: string;
  source?: string;
  /** Set once an admin staged the announcement as a product (flag `preorder_lifecycle`). */
  stagedProductId?: string;
};

type StageForm = {
  sku: string;
  model: string;
  name: string;
  productType: 'camera' | 'lens' | 'accessory';
  /** Catalog category slug; the server defaults a camera to `mirrorless` and requires one for a lens or accessory. */
  category: string;
  releaseDate: string;
  preorderOpensAt: string;
};

/** Categories offered per type; a slug the catalog already uses, never free text. */
const STAGE_CATEGORIES: Record<StageForm['productType'], Array<{ value: string; label: string }>> = {
  camera: [
    { value: 'mirrorless', label: 'Mirrorless' },
    { value: 'dslr', label: 'DSLR' },
    { value: 'compact', label: 'Compact' },
    { value: 'medium-format', label: 'Medium format' },
    { value: 'action-camera', label: 'Action camera' },
    { value: 'video', label: 'Video / cinema' },
    { value: 'instant', label: 'Instant' },
    { value: 'film', label: 'Film' },
  ],
  lens: [
    { value: 'lens-prime', label: 'Prime lens' },
    { value: 'lens-zoom', label: 'Zoom lens' },
    { value: 'lens-macro', label: 'Macro lens' },
    { value: 'lens-telephoto', label: 'Telephoto lens' },
    { value: 'lens-wide-angle', label: 'Wide-angle lens' },
    { value: 'lens-cine', label: 'Cine lens' },
  ],
  // Every slug here is an accessory in `productCategoryTaxonomy` (a teleconverter is a lens accessory, not a lens).
  accessory: [
    { value: 'teleconverter', label: 'Teleconverter' },
    { value: 'flash', label: 'Flash' },
    { value: 'tripod', label: 'Tripod' },
    { value: 'gimbal', label: 'Gimbal' },
    { value: 'microphone', label: 'Microphone' },
    { value: 'monitor', label: 'Monitor' },
    { value: 'battery-grip', label: 'Battery grip' },
    { value: 'bag', label: 'Bag' },
    { value: 'filter', label: 'Filter' },
    { value: 'other', label: 'Other accessory' },
  ],
};

const emptyStageForm = (signal: SignalRow): StageForm => ({
  sku: '',
  model: '',
  name: signal.title || '',
  productType: 'camera',
  category: 'mirrorless',
  releaseDate: '',
  preorderOpensAt: '',
});

function sourceLabel(source?: string): string {
  if (source === 'article') return 'Article';
  if (source === 'manual') return 'Manual';
  return 'Scrape';
}

function matchesFilter(status: string, filter: SignalFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'queued') return status === 'scrape_queued';
  return ['new', 'watching', 'scrape_queued'].includes(status);
}

/** Six tracks: checkbox, brand, signal, state, source, actions. */
const COLUMNS = '34px 128px minmax(0, 1fr) 118px 88px 220px';

const HEADER_CELL = {
  fontFamily: ingest.font.sans,
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.07em',
  textTransform: 'uppercase' as const,
  color: ingest.color.faintAlt,
};

const SelectBox: React.FC<{
  checked: boolean;
  /** Some but not all rows selected — screen readers need "mixed", not "false". */
  indeterminate?: boolean;
  label: string;
  onToggle: () => void;
}> = ({ checked, indeterminate = false, label, onToggle }) => (
  <Box
    component="button"
    type="button"
    role="checkbox"
    aria-checked={indeterminate ? 'mixed' : checked}
    aria-label={label}
    onClick={onToggle}
    sx={{
      width: 16,
      height: 16,
      padding: 0,
      border: `1px solid ${checked ? ingest.color.accent : tokens.ink.disabled}`,
      background: checked ? ingest.color.accent : ingest.color.surface,
      borderRadius: '4px',
      cursor: 'pointer',
      color: ingest.color.surface,
      fontSize: '10px',
      lineHeight: 1,
      display: 'grid',
      placeItems: 'center',
    }}
  >
    {checked ? '✓' : indeterminate ? '–' : ''}
  </Box>
);

export const AnnouncementCandidatesPanel: React.FC = () => {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<string | null>(null);
  const [filter, setFilter] = useState<SignalFilter>('open');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // "Stage as announced" (flag `preorder_lifecycle`, MOT-143): one signal at a time, never automatic.
  const [stageTarget, setStageTarget] = useState<SignalRow | null>(null);
  const [stageForm, setStageForm] = useState<StageForm | null>(null);
  const [stageError, setStageError] = useState<string | null>(null);

  const { data = [], isLoading } = useQuery({
    queryKey: ['announcement-candidates'],
    queryFn: () => integrationApiService.fetchAnnouncementCandidates(undefined, 80),
  });

  const runMonitor = useMutation({
    mutationFn: () => integrationApiService.runAnnouncementMonitor(),
    onSuccess: (result) => {
      setMessage(`Monitor: ${result.candidatesCreated || 0} new candidates, ${result.scrapesDispatched || 0} scrapes`);
      queryClient.invalidateQueries({ queryKey: ['announcement-candidates'] });
    },
    onError: (err: Error) => setMessage(err.message),
  });

  const dismiss = useMutation({
    mutationFn: (id: string) => integrationApiService.dismissAnnouncementCandidate(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['announcement-candidates'] }),
  });

  const scrape = useMutation({
    mutationFn: (id: string) => integrationApiService.scrapeAnnouncementCandidate(id),
    onSuccess: (jobId) => {
      setMessage(`Scrape queued (job ${jobId})`);
      queryClient.invalidateQueries({ queryKey: ['announcement-candidates'] });
    },
    onError: (err: Error) => setMessage(err.message),
  });

  const stage = useMutation({
    mutationFn: async ({ id, form }: { id: string; form: StageForm }) =>
      integrationApiService.stageAnnouncementCandidate(id, {
        sku: form.sku.trim(),
        model: form.model.trim(),
        name: form.name.trim() || undefined,
        productType: form.productType,
        category: form.category,
        releaseDate: form.releaseDate || null,
        preorderOpensAt: form.preorderOpensAt || null,
      }),
    onSuccess: (result) => {
      setMessage(`Staged as ${result.stagedProductId} (lifecycle ${result.lifecycle?.stage || 'announced'}); it is in the staged review queue.`);
      setStageTarget(null);
      setStageForm(null);
      setStageError(null);
      queryClient.invalidateQueries({ queryKey: ['announcement-candidates'] });
    },
    onError: (err: Error) => setStageError(err.message),
  });

  const openStageDialog = (signal: SignalRow) => {
    setStageTarget(signal);
    setStageForm(emptyStageForm(signal));
    setStageError(null);
  };
  const stageDatesInvalid =
    !!stageForm?.releaseDate && !!stageForm?.preorderOpensAt && stageForm.preorderOpensAt > stageForm.releaseDate;

  const bulkScrape = useMutation({
    mutationFn: async (ids: string[]) => {
      const results = await Promise.allSettled(
        ids.map((id) => integrationApiService.scrapeAnnouncementCandidate(id))
      );
      const queued = results.filter((result) => result.status === 'fulfilled').length;
      const failed = results.length - queued;
      return { queued, failed };
    },
    onSuccess: ({ queued, failed }) => {
      setMessage(
        failed > 0
          ? `Queued ${queued} scrape${queued === 1 ? '' : 's'}; ${failed} failed`
          : `Queued ${queued} scrape${queued === 1 ? '' : 's'}`
      );
      setSelected(new Set());
      queryClient.invalidateQueries({ queryKey: ['announcement-candidates'] });
    },
    onError: (err: Error) => setMessage(err.message),
  });

  const bulkDismiss = useMutation({
    mutationFn: async (ids: string[]) => {
      await Promise.all(ids.map((id) => integrationApiService.dismissAnnouncementCandidate(id)));
      return ids.length;
    },
    onSuccess: (count) => {
      setMessage(`Dismissed ${count} signal${count === 1 ? '' : 's'}`);
      setSelected(new Set());
      queryClient.invalidateQueries({ queryKey: ['announcement-candidates'] });
    },
    onError: (err: Error) => setMessage(err.message),
  });

  const counts = useMemo(() => {
    const open = data.filter((c: { status?: string }) =>
      ['new', 'watching', 'scrape_queued'].includes(String(c.status || ''))
    ).length;
    const queued = data.filter((c: { status?: string }) => c.status === 'scrape_queued').length;
    return { open, queued, all: data.length };
  }, [data]);

  const visible = data.filter((c: SignalRow) => matchesFilter(String(c.status || ''), filter));
  const selectedItems = data.filter((c: SignalRow) => selected.has(c.id));
  const scrapableSelected = selectedItems.filter((c: SignalRow) => Boolean(c.productUrl));
  const allVisibleSelected = visible.length > 0 && visible.every((c: SignalRow) => selected.has(c.id));
  const someVisibleSelected = visible.some((c: SignalRow) => selected.has(c.id));
  const bulkPending = bulkScrape.isPending || bulkDismiss.isPending;

  const toggleAll = () => {
    if (allVisibleSelected) {
      setSelected(new Set());
      return;
    }
    setSelected(new Set(visible.map((c: { id: string }) => c.id)));
  };

  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const chips: Array<{ id: SignalFilter; label: string; count: number }> = [
    { id: 'open', label: 'Open', count: counts.open },
    { id: 'queued', label: 'Queued', count: counts.queued },
    { id: 'all', label: 'All', count: counts.all },
  ];

  return (
    <Box
      sx={{
        border: `1px solid ${ingest.color.border}`,
        borderRadius: '10px',
        background: ingest.color.surface,
        boxShadow: ingest.shadow.card,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          padding: '16px 20px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '14px',
          flexWrap: 'wrap',
          borderBottom: `1px solid ${ingest.color.borderInner}`,
        }}
      >
        <Box>
          <Box sx={{ fontFamily: ingest.font.sans, fontSize: '15px', fontWeight: 600, color: ingest.color.ink }}>
            Signals &amp; watchlist
          </Box>
          <Box
            sx={{ fontFamily: ingest.font.sans, fontSize: '12.5px', color: ingest.color.faint, marginTop: '3px' }}
          >
            Press coverage that may mean new products. Scrape queues a targeted run; dismiss drops the signal.
          </Box>
        </Box>
        <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          {chips.map((chip) => {
            const tone = chipTone(filter === chip.id);
            return (
              <Box
                key={chip.id}
                component="button"
                type="button"
                aria-pressed={filter === chip.id}
                onClick={() => setFilter(chip.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  height: 30,
                  padding: '0 11px',
                  border: `1px solid ${tone.border}`,
                  background: tone.bg,
                  borderRadius: '15px',
                  fontFamily: ingest.font.sans,
                  fontSize: '12px',
                  fontWeight: tone.weight,
                  color: tone.ink,
                  cursor: 'pointer',
                  '&:hover': { borderColor: filter === chip.id ? tone.border : tokens.ink.disabled },
                }}
              >
                <span>{chip.label}</span>
                <Box component="span" sx={{ ...tabular, color: tone.countInk }}>
                  {chip.count}
                </Box>
              </Box>
            );
          })}
          <Button
            disableElevation
            sx={secondaryButton}
            onClick={() => runMonitor.mutate()}
            disabled={runMonitor.isPending}
            startIcon={runMonitor.isPending ? <CircularProgress size={13} /> : undefined}
          >
            Run monitor
          </Button>
        </Box>
      </Box>

      {message && (
        <Alert severity="info" sx={{ margin: '14px 20px 0' }} onClose={() => setMessage(null)}>
          {message}
        </Alert>
      )}

      {selected.size > 0 && (
        <Box
          sx={{
            margin: '14px 20px 0',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            minHeight: 44,
            padding: '0 14px',
            background: ingest.color.chipOn,
            borderRadius: '8px',
            flexWrap: 'wrap',
          }}
        >
          <Box
            sx={{
              fontFamily: ingest.font.sans,
              fontSize: '12.5px',
              fontWeight: 600,
              color: ingest.color.surface,
              ...tabular,
            }}
          >
            {selected.size} signal{selected.size === 1 ? '' : 's'} selected
          </Box>
          <Box sx={{ width: '1px', height: 18, background: ingest.color.chipOnDivider }} />
          <Button
            disableElevation
            sx={{
              ...primaryButton,
              height: 28,
              minHeight: 28,
              px: '11px',
              borderRadius: '6px',
              fontSize: '12px',
              // Disabled on the dark bulk bar, not on white. No token in the shared
              // set covers a disabled fill on a dark surface, so this stays literal.
              /* eslint-disable-next-line no-restricted-syntax -- bespoke dark-bar disabled fill, no semantic token equivalent */
              '&.Mui-disabled': { background: '#2a3550', color: ingest.color.chipOnCount },
            }}
            disabled={scrapableSelected.length === 0 || bulkPending}
            onClick={() => bulkScrape.mutate(scrapableSelected.map((c) => c.id))}
          >
            {bulkScrape.isPending
              ? 'Scraping…'
              : `Scrape selected${scrapableSelected.length ? ` (${scrapableSelected.length})` : ''}`}
          </Button>
          <Button
            disableElevation
            disabled={bulkPending}
            onClick={() => bulkDismiss.mutate(selectedItems.map((c) => c.id))}
            sx={{
              height: 28,
              minHeight: 28,
              px: '11px',
              border: `1px solid ${ingest.color.chipOnDivider}`,
              borderRadius: '6px',
              background: 'transparent',
              fontFamily: ingest.font.sans,
              fontSize: '12px',
              color: ingest.color.chipOnBody,
              textTransform: 'none',
              '&:hover': { background: 'transparent', borderColor: tokens.ink.muted },
              '&.Mui-disabled': { color: ingest.color.muted, borderColor: ingest.color.chipOnDivider },
            }}
          >
            {bulkDismiss.isPending ? 'Dismissing…' : 'Dismiss'}
          </Button>
          <Button
            disableElevation
            onClick={() => setSelected(new Set())}
            sx={{
              marginLeft: 'auto',
              height: 28,
              minHeight: 28,
              px: '10px',
              border: 0,
              background: 'transparent',
              fontFamily: ingest.font.sans,
              fontSize: '12px',
              color: ingest.color.faint,
              textTransform: 'none',
              '&:hover': { background: 'transparent', color: ingest.color.surface },
            }}
          >
            Clear
          </Button>
        </Box>
      )}

      <Box sx={{ overflowX: { xs: 'visible', md: 'auto' } }}>
        <Box role="table" aria-label="Signals and watchlist" sx={{ minWidth: { xs: 0, md: 860 } }}>
          <Box
            role="row"
            sx={{
              display: { xs: 'none', md: 'grid' },
              gridTemplateColumns: { xs: '1fr', md: COLUMNS },
              gap: '14px',
              alignItems: 'center',
              padding: '14px 20px 8px',
              borderBottom: `1px solid ${ingest.color.borderInner}`,
              ...HEADER_CELL,
            }}
          >
            <Box role="columnheader" aria-label="Select">
              {visible.length > 0 && (
                <SelectBox
                  checked={allVisibleSelected}
                  indeterminate={someVisibleSelected && !allVisibleSelected}
                  label="Select all signals"
                  onToggle={toggleAll}
                />
              )}
            </Box>
            <Box role="columnheader">Brand</Box>
            <Box role="columnheader">Signal</Box>
            <Box role="columnheader">State</Box>
            <Box role="columnheader">Source</Box>
            <Box role="columnheader" aria-label="Actions" />
          </Box>

          {isLoading && (
            <Box sx={{ padding: '24px 20px', display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={22} />
            </Box>
          )}

          {!isLoading && visible.length === 0 && (
            <Box
              sx={{ padding: '20px', fontFamily: ingest.font.sans, fontSize: '12.5px', color: ingest.color.faintAlt }}
            >
              No signals in this filter. Daily listing discovery still runs at 02:00 UTC.
            </Box>
          )}

          {visible.map((signal: SignalRow) => {
            const checked = selected.has(signal.id);
            const queued = signal.status === 'scrape_queued';
            return (
              <Box
                key={signal.id}
                role="row"
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'minmax(0,1fr)', md: COLUMNS },
                  gap: { xs: '8px', md: '14px' },
                  alignItems: { xs: 'stretch', md: 'center' },
                  padding: '10px 20px',
                  borderBottom: `1px solid ${ingest.color.borderFaintAlt}`,
                  background: checked ? tokens.accent.tint : ingest.color.surface,
                  '&:hover': { background: checked ? tokens.accent.tint : ingest.color.rowOpen },
                }}
              >
                <Box role="cell" sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                  <SelectBox
                    checked={checked}
                    label={`Select ${signal.title || signal.id}`}
                    onToggle={() => toggleOne(signal.id)}
                  />
                  <Box
                    sx={{
                      display: { xs: 'block', md: 'none' },
                      fontFamily: ingest.font.sans,
                      fontSize: '12.5px',
                      fontWeight: 500,
                      color: ingest.color.ink,
                      ...ellipsis,
                    }}
                  >
                    {signal.brandName || signal.brandId}
                  </Box>
                </Box>
                <Box
                  role="cell"
                  sx={{
                    display: { xs: 'none', md: 'block' },
                    fontFamily: ingest.font.sans,
                    fontSize: '12.5px',
                    fontWeight: 500,
                    color: ingest.color.ink,
                    ...ellipsis,
                  }}
                >
                  {signal.brandName || signal.brandId}
                </Box>
                <Box role="cell" sx={{ minWidth: 0 }}>
                  <Link
                    href={signal.articleUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{
                      display: 'block',
                      fontFamily: ingest.font.sans,
                      fontSize: '13px',
                      color: ingest.color.accent,
                      ...ellipsis,
                    }}
                  >
                    {signal.title}
                  </Link>
                  {signal.productUrl && (
                    <Box
                      sx={{
                        fontFamily: ingest.font.mono,
                        fontSize: '11px',
                        color: ingest.color.faintAlt,
                        marginTop: '2px',
                        ...ellipsis,
                      }}
                    >
                      PDP: {signal.productUrl}
                    </Box>
                  )}
                </Box>
                <Box role="cell" sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-block',
                      height: 21,
                      padding: '0 9px',
                      borderRadius: '11px',
                      background: queued ? ingest.color.queuedTint : ingest.color.accentTint,
                      color: queued ? ingest.color.queuedInk : ingest.color.accentHover,
                      fontFamily: ingest.font.sans,
                      fontSize: '11.5px',
                      fontWeight: 500,
                      lineHeight: '21px',
                    }}
                  >
                    {signal.status || 'watching'}
                  </Box>
                  <Box
                    sx={{
                      display: { xs: 'inline-block', md: 'none' },
                      fontFamily: ingest.font.sans,
                      fontSize: '12px',
                      color: ingest.color.faint,
                    }}
                  >
                    · {sourceLabel(signal.source)}
                  </Box>
                </Box>
                <Box
                  role="cell"
                  sx={{
                    display: { xs: 'none', md: 'block' },
                    fontFamily: ingest.font.sans,
                    fontSize: '12px',
                    color: ingest.color.faint,
                  }}
                >
                  {sourceLabel(signal.source)}
                </Box>
                <Box role="cell" sx={{ display: 'flex', alignItems: 'center', gap: '7px', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                  <Button
                    disableElevation
                    sx={{ ...rowButton, height: 27, minHeight: 27, px: '11px' }}
                    disabled={!!signal.stagedProductId || signal.status === 'dismissed' || signal.status === 'expired' || stage.isPending}
                    onClick={() => openStageDialog(signal)}
                    title={signal.stagedProductId ? `Staged as ${signal.stagedProductId}` : 'Create a staged product with lifecycle Announced'}
                  >
                    {signal.stagedProductId ? 'Staged' : 'Stage'}
                  </Button>
                  <Button
                    disableElevation
                    sx={{ ...rowButton, height: 27, minHeight: 27, px: '11px' }}
                    disabled={!signal.productUrl || scrape.isPending}
                    onClick={() => scrape.mutate(signal.id)}
                  >
                    Scrape
                  </Button>
                  <Button
                    disableElevation
                    onClick={() => dismiss.mutate(signal.id)}
                    disabled={dismiss.isPending}
                    sx={{
                      height: 27,
                      minHeight: 27,
                      px: '9px',
                      border: 0,
                      background: 'transparent',
                      fontFamily: ingest.font.sans,
                      fontSize: '12px',
                      color: ingest.color.faint,
                      textTransform: 'none',
                      '&:hover': { background: 'transparent', color: ingest.color.body },
                    }}
                  >
                    Dismiss
                  </Button>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box sx={{ padding: '12px 20px', fontFamily: ingest.font.sans, fontSize: '12px', color: ingest.color.faint }}>
        Showing {visible.length} of {data.length} signals · dismissed signals are kept for 30 days
      </Box>

      <FormDialog
        open={!!stageTarget && !!stageForm}
        title="Stage this announcement as a product?"
        onClose={() => {
          if (stage.isPending) return;
          setStageTarget(null);
          setStageForm(null);
        }}
        onSubmit={() => stageTarget && stageForm && stage.mutate({ id: stageTarget.id, form: stageForm })}
        submitLabel="Stage as announced"
        submitDisabled={!stageForm?.sku.trim() || !stageForm?.model.trim() || !stageForm?.category || stageDatesInvalid}
        loading={stage.isPending}
      >
        {stageTarget && stageForm && (
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              A staged product for {stageTarget.brandName || stageTarget.brandId} goes to the review queue with lifecycle
              Announced. Stores with the pre-release lifecycle turned on see it in their catalog once it is promoted;
              specs, images and pricing arrive when the manufacturer page is scraped. Nothing is written to any store.
            </Typography>
            <TextField
              label="SKU or model code"
              size="small"
              required
              value={stageForm.sku}
              onChange={(e) => setStageForm({ ...stageForm, sku: e.target.value })}
              helperText="With the brand, this makes the product id; a later scrape of the same SKU merges into it."
            />
            <TextField
              label="Model"
              size="small"
              required
              value={stageForm.model}
              onChange={(e) => setStageForm({ ...stageForm, model: e.target.value })}
            />
            <TextField
              label="Product name"
              size="small"
              value={stageForm.name}
              onChange={(e) => setStageForm({ ...stageForm, name: e.target.value })}
              helperText="Defaults to the announcement headline."
            />
            <TextField
              select
              label="Product type"
              size="small"
              value={stageForm.productType}
              onChange={(e) => {
                const productType = e.target.value as StageForm['productType'];
                // The category follows the type: the first slug offered for it.
                setStageForm({ ...stageForm, productType, category: STAGE_CATEGORIES[productType][0].value });
              }}
            >
              <MenuItem value="camera">Camera</MenuItem>
              <MenuItem value="lens">Lens</MenuItem>
              <MenuItem value="accessory">Accessory</MenuItem>
            </TextField>
            <TextField
              select
              label="Category"
              size="small"
              required
              value={stageForm.category}
              onChange={(e) => setStageForm({ ...stageForm, category: e.target.value })}
              helperText="The catalog category the product files under"
            >
              {STAGE_CATEGORIES[stageForm.productType].map((c) => (
                <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
              ))}
            </TextField>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <DateField
                label="Pre-orders open"
                size="small"
                value={stageForm.preorderOpensAt}
                onChange={(v) => setStageForm({ ...stageForm, preorderOpensAt: v })}
                error={stageDatesInvalid}
                helperText={stageDatesInvalid ? 'Must be on or before the release date' : 'Leave empty if unknown'}
                sx={{ flex: 1 }}
              />
              <DateField
                label="Release date"
                size="small"
                value={stageForm.releaseDate}
                onChange={(v) => setStageForm({ ...stageForm, releaseDate: v })}
                helperText="The daily sweep marks it Released on this day"
                sx={{ flex: 1 }}
              />
            </Stack>
            {stageError && <Alert severity="error">{stageError}</Alert>}
          </Stack>
        )}
      </FormDialog>
    </Box>
  );
};

export default AnnouncementCandidatesPanel;
